import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

// Components
import Spinner from "../Components/Spinner";
import { ToastComponent } from "../Components/ToastComponent";
import EmptyData from "../../components/EmptyData";

// Services
import { clientUserService } from "../../Services/clientUsers.service";
import { profilePermissionsService } from "../../Services/profile-permissions.service";
import { storage } from "../../Services/storage.service";
import { storageKeys } from "../../Constants/storage.key";

const normalizeActive = (value) => value === "S" || value === true;

// GET /menu-permissions/access-levels/{id} devolve uma lista plana
// (MenuId, Level, Name, Active), nível 1 = grupo, nível 2 = item do grupo,
// nessa ordem. Agrupa localmente pra exibir como na planilha de referência.
const buildPermissionGroups = (items) => {
  const groups = [];
  let current = null;

  items.forEach((raw) => {
    const menuId = raw.menuId ?? raw.MenuId;
    const level = raw.level ?? raw.Level;
    const name = raw.description ?? raw.Description ?? raw.name ?? raw.Name;
    const active = normalizeActive(raw.active ?? raw.Active);
    const normalized = { menuId, name, active };

    if (level === 1) {
      current = { ...normalized, children: [] };
      groups.push(current);
    } else if (current) {
      current.children.push(normalized);
    } else {
      // Item nível 2 sem grupo anterior (não deveria acontecer, mas evita perder o dado).
      current = { menuId: `orphan-${menuId}`, name: "", active: false, children: [normalized] };
      groups.push(current);
    }
  });

  return groups;
};

// Clona a estrutura (nomes/hierarquia) de um catálogo já carregado, zerando
// todos os "Active" — usado para montar o formulário de um perfil ainda não
// criado, sem precisar de um accessLevelId real.
const cloneGroupsAsUnchecked = (groups) =>
  groups.map((group) => ({
    ...group,
    active: false,
    children: group.children.map((child) => ({ ...child, active: false })),
  }));

export default function AccessProfile() {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [userSessionData] = useState(storage.get(storageKeys.settings));
  const [profiles, setProfiles] = useState([]);
  const [selectedLevelId, setSelectedLevelId] = useState(null);
  const [selectedLevelName, setSelectedLevelName] = useState("");
  const [newProfileName, setNewProfileName] = useState("");
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [catalogGroups, setCatalogGroups] = useState([]);
  const [isDraftProfile, setIsDraftProfile] = useState(false);
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [updatingMenuIds, setUpdatingMenuIds] = useState(() => new Set());
  const [toastParams, setToastParams] = useState("");

  React.useEffect(() => {
    dispatch({ type: "title", value: t("ACCESS_PROFILE.TITLE") });
    loadProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const showToast = (title, body, color) => {
    setToastParams(JSON.stringify({ show: true, title, body, color }));
  };

  const loadProfiles = async () => {
    const userLogId = userSessionData?.userLogId;
    if (!userLogId) return;

    setIsLoadingProfiles(true);
    try {
      const response = await clientUserService.getUserLevels(userLogId);
      setProfiles(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Erro ao buscar perfis de acesso:", error);
      setProfiles([]);
    } finally {
      setIsLoadingProfiles(false);
    }
  };

  const handleSelectProfile = async (profile) => {
    const levelId = profile.levelId ?? profile.LevelId;
    const levelName = profile.levelName ?? profile.LevelName ?? "";

    setIsDraftProfile(false);
    setSelectedLevelId(levelId);
    setSelectedLevelName(levelName);
    setPermissionGroups([]);
    setIsLoadingPermissions(true);

    try {
      const response = await profilePermissionsService.fetchMenuPermissions(levelId);
      const items = Array.isArray(response) ? response : Array.isArray(response?.items) ? response.items : [];
      const groups = buildPermissionGroups(items);
      setPermissionGroups(groups);
      // Guarda a estrutura (nomes/grupos) pra poder reaproveitar na criação
      // de um novo perfil, sem precisar de accessLevelId pra isso.
      if (groups.length > 0) {
        setCatalogGroups(groups);
      }
    } catch (error) {
      console.error("Erro ao buscar permissões do perfil:", error);
      showToast(t("COMMON.ERROR"), t("ACCESS_PROFILE.LOAD_PERMISSIONS_ERROR"), "Danger");
      setPermissionGroups([]);
    } finally {
      setIsLoadingPermissions(false);
    }
  };

  // O checkbox do grupo (nível 1) reflete automaticamente os filhos: fica
  // marcado se pelo menos um item do grupo estiver marcado, desmarcado se
  // nenhum estiver.
  const isGroupActive = (group) => group.children.some((child) => child.active);

  const setGroupChildrenLocal = (group, nextActive) => {
    setPermissionGroups((prev) =>
      prev.map((g) =>
        g.menuId !== group.menuId
          ? g
          : { ...g, children: g.children.map((c) => ({ ...c, active: nextActive })) }
      )
    );
  };

  const toggleItemLocal = (group, item, nextActive) => {
    setPermissionGroups((prev) =>
      prev.map((g) =>
        g.menuId !== group.menuId
          ? g
          : {
              ...g,
              children: g.children.map((c) =>
                c.menuId === item.menuId ? { ...c, active: nextActive } : c
              ),
            }
      )
    );
  };

  const toggleItem = async (group, item) => {
    const nextActive = !item.active;

    // Perfil ainda não criado: só mantém a marcação localmente, sem chamar API.
    if (isDraftProfile) {
      toggleItemLocal(group, item, nextActive);
      return;
    }


    const wasGroupActive = isGroupActive(group);
    const willGroupBeActive = group.children.some((child) =>
      child.menuId === item.menuId ? nextActive : child.active
    );
    const groupChanged = wasGroupActive !== willGroupBeActive;

    toggleItemLocal(group, item, nextActive);
    setUpdatingMenuIds((prev) => {
      const next = new Set(prev).add(item.menuId);
      if (groupChanged) next.add(group.menuId);
      return next;
    });

    try {
      const calls = [profilePermissionsService.setMenuPermission(selectedLevelId, item.menuId, nextActive)];
      if (groupChanged) {
        calls.push(profilePermissionsService.setMenuPermission(selectedLevelId, group.menuId, willGroupBeActive));
      }
      const results = await Promise.all(calls);

      const anyFailed = results.some((result) => !result || result?.error);

      if (anyFailed) {
        toggleItemLocal(group, item, !nextActive);
        showToast(t("COMMON.ERROR"), t("ACCESS_PROFILE.PERMISSION_SAVE_ERROR"), "Danger");
        return;
      }

      showToast(t("COMMON.SUCCESS"), t("ACCESS_PROFILE.PERMISSION_SAVED_SUCCESS"), "Success");
    } catch (error) {
      toggleItemLocal(group, item, !nextActive);
      showToast(t("COMMON.ERROR"), t("ACCESS_PROFILE.PERMISSION_SAVE_ERROR"), "Danger");
    } finally {
      setUpdatingMenuIds((prev) => {
        const next = new Set(prev);
        next.delete(item.menuId);
        if (groupChanged) next.delete(group.menuId);
        return next;
      });
    }
  };

  // Clicar no grupo marca/desmarca TODOS os filhos de uma vez. O estado do
  // grupo é sempre derivado (isGroupActive) — aqui só decidimos pra qual
  // lado vamos: se já tinha algo marcado, desmarca tudo; senão, marca tudo.
  const toggleGroup = async (group) => {
    const nextActive = !isGroupActive(group);

    setGroupChildrenLocal(group, nextActive);

    if (isDraftProfile) return;

    const menuIds = [group.menuId, ...group.children.map((child) => child.menuId)];
    setUpdatingMenuIds((prev) => {
      const next = new Set(prev);
      menuIds.forEach((id) => next.add(id));
      return next;
    });

    try {
      const results = await Promise.all(
    menuIds.map((menuId) =>
    profilePermissionsService.setMenuPermission(selectedLevelId, menuId, nextActive)
  )
);

      const anyFailed = results.some((result) => !result || result?.error);

      if (anyFailed) {
        setGroupChildrenLocal(group, !nextActive);
        showToast(t("COMMON.ERROR"), t("ACCESS_PROFILE.PERMISSION_SAVE_ERROR"), "Danger");
        return;
      }

      showToast(t("COMMON.SUCCESS"), t("ACCESS_PROFILE.PERMISSION_SAVED_SUCCESS"), "Success");
    } catch (error) {
      setGroupChildrenLocal(group, !nextActive);
      showToast(t("COMMON.ERROR"), t("ACCESS_PROFILE.PERMISSION_SAVE_ERROR"), "Danger");
    } finally {
      setUpdatingMenuIds((prev) => {
        const next = new Set(prev);
        menuIds.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  // Só monta o formulário de permissões localmente (reaproveitando o
  // catálogo já carregado de outro perfil) — não chama a API de criação
  // aqui, ela só é chamada quando o usuário confirmar as permissões.
  // Input/botão "Criar Perfil" comentados temporariamente (ver JSX abaixo);
  // mantido aqui pronto pra quando voltarem.
  // eslint-disable-next-line no-unused-vars
  const handleStartCreateProfile = () => {
    const name = newProfileName.trim();
    if (!name) {
      showToast(t("COMMON.ATTENTION"), t("ACCESS_PROFILE.PROFILE_NAME_REQUIRED"), "Warning");
      return;
    }

    if (catalogGroups.length === 0) {
      showToast(t("COMMON.ATTENTION"), t("ACCESS_PROFILE.CATALOG_NOT_LOADED_HINT"), "Warning");
      return;
    }

    setIsDraftProfile(true);
    setSelectedLevelId(null);
    setSelectedLevelName(name);
    setPermissionGroups(cloneGroupsAsUnchecked(catalogGroups));
    setNewProfileName("");
  };

  // Sai da visualização/rascunho atual e volta pro estado neutro — assim dá
  // pra clicar em "Criar" de novo sem herdar nada do perfil que estava aberto.
  const handleCancelSelection = () => {
    setIsDraftProfile(false);
    setSelectedLevelId(null);
    setSelectedLevelName("");
    setPermissionGroups([]);
  };

  const handleConfirmCreateProfile = async () => {
    setIsCreating(true);
    try {
      const response = await profilePermissionsService.createProfile(selectedLevelName);
      const newLevelId = response?.levelId ?? response?.LevelId ?? response?.id ?? response?.Id;

      if (!response || !newLevelId) {
        showToast(t("COMMON.ERROR"), t("ACCESS_PROFILE.PROFILE_CREATE_ERROR"), "Danger");
        return;
      }

      const checkedItems = permissionGroups.flatMap((group) =>
        group.children.filter((child) => child.active)
      );

      await Promise.all(
        checkedItems.map((item) =>
          profilePermissionsService.setMenuPermission(newLevelId, item.menuId, true)
        )
      );

      showToast(t("COMMON.SUCCESS"), t("ACCESS_PROFILE.PROFILE_CREATED_SUCCESS"), "Success");
      setIsDraftProfile(false);
      setSelectedLevelId(newLevelId);
      await loadProfiles();
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />

      {(isLoadingProfiles || isCreating) && <Spinner />}

      <div className="card p-3 mb-3">
        <h5 className="mb-3">{t("ACCESS_PROFILE.CREATED_PROFILES")}</h5>
        <div className="d-flex flex-wrap gap-2">
          {profiles.map((profile) => {
            const levelId = profile.levelId ?? profile.LevelId;
            const levelName = profile.levelName ?? profile.LevelName ?? "";
            const isActive = !isDraftProfile && selectedLevelId === levelId;

            return (
              <button
                key={levelId}
                type="button"
                className={`btn btn-sm ${isActive ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => handleSelectProfile(profile)}
              >
                {levelName}
              </button>
            );
          })}
        </div>
      </div>

      {/* Criar Perfil — comentado temporariamente, volta futuramente.
      <div className="card p-3 mb-3">
        <h5 className="mb-3">{t("ACCESS_PROFILE.CREATE_PROFILE")}</h5>
        <div className="row g-2 align-items-center">
          <div className="col-sm-4">
            <input
              type="text"
              className="form-control"
              placeholder={t("ACCESS_PROFILE.CREATE_PROFILE_PLACEHOLDER")}
              value={newProfileName}
              onChange={(e) => setNewProfileName(e.target.value)}
            />
          </div>
          <div className="col-sm-auto">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleStartCreateProfile}
            >
              {t("ACCESS_PROFILE.CREATE_BUTTON")}
            </button>
          </div>
        </div>
      </div>
      */}

      <div className="card p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">
            {t("ACCESS_PROFILE.SELECT_PERMISSIONS")}{" "}
            <strong>{selectedLevelName || "—"}</strong>
          </h5>

          {(selectedLevelId || isDraftProfile) && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleCancelSelection}
            >
              {t("ACCESS_PROFILE.CANCEL_SELECTION")}
            </button>
          )}
        </div>

        {!selectedLevelId && !isDraftProfile ? (
          <p className="text-muted mb-0">{t("ACCESS_PROFILE.SELECT_PROFILE_HINT")}</p>
        ) : isLoadingPermissions ? (
          <Spinner />
        ) : permissionGroups.length === 0 ? (
          <EmptyData />
        ) : (
          <>
            {isDraftProfile && (
              <p className="text-muted">{t("ACCESS_PROFILE.DRAFT_HINT")}</p>
            )}

            <div className="row">
              {permissionGroups.map((group) => (
                <div className="col-md-4 mb-4" key={group.menuId}>
                  <div className="form-check ps-0 mb-2">
                    <input
                      className="form-check-input ms-0 me-2"
                      type="checkbox"
                      id={`group-${group.menuId}`}
                      checked={isGroupActive(group)}
                      disabled={group.children.some((child) => updatingMenuIds.has(child.menuId))}
                      onChange={() => toggleGroup(group)}
                    />
                    <label className="form-check-label fw-bold" htmlFor={`group-${group.menuId}`}>
                      {group.name}
                    </label>
                  </div>

                  {group.children.map((child) => (
                    <div className="form-check ps-5 mb-1" key={child.menuId}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`item-${child.menuId}`}
                        checked={child.active}
                        disabled={updatingMenuIds.has(child.menuId)}
                        onChange={() => toggleItem(group, child)}
                      />
                      <label className="form-check-label" htmlFor={`item-${child.menuId}`}>
                        {child.name}
                      </label>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {isDraftProfile && (
              <div className="d-flex justify-content-end mt-2">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={isCreating}
                  onClick={handleConfirmCreateProfile}
                >
                  {t("ACCESS_PROFILE.CONFIRM_CREATE_PROFILE")}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
